import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Button, Spinner } from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import ApprovalCommentService from '../../../../services/ApprovalComment';
import { toast } from 'react-toastify';
import moment from 'moment';

interface ApprovalChatModalProps {
  isOpen: boolean;
  toggle: () => void;
  approvalType: string;
  approvalId: string;
  title?: string;
  schoolId?: string;
}

const ApprovalChatModal: React.FC<ApprovalChatModalProps> = ({
  isOpen,
  toggle,
  approvalType,
  approvalId,
  title,
  schoolId,
}) => {
  const { user, token }: any = useContext(AuthContext);
  const [comments, setComments] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadComments = async () => {
    if (!approvalId) return;
    setLoading(true);
    try {
      const data = await ApprovalCommentService(token).list(approvalType, approvalId);
      setComments(data);
    } catch {
      toast.error('Failed to load comments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && approvalId) {
      loadComments();
    }
  }, [isOpen, approvalId]);

  useEffect(() => {
    if (comments.length) scrollToBottom();
  }, [comments]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const comment = await ApprovalCommentService(token).create(approvalType, approvalId, trimmed, schoolId);
      setComments((prev) => [...prev, comment]);
      setText('');
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const myId = user?.id;

  const formatChatDate = (date: string | Date) => {
    const m = moment(date);
    const now = moment();
    if (m.isSame(now, 'day')) return `Today at ${m.format('h:mm A')}`;
    if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return `Yesterday at ${m.format('h:mm A')}`;
    return `${m.format('MM/DD/YYYY')} at ${m.format('h:mm A')}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
      <ModalHeader toggle={toggle} tag="div">
        <div>
          <h5 className="mb-0 modal-title">{title || 'Chat'}</h5>
          <div className="text-muted small">Ask questions or share information about this request</div>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: 0 }}>
        <div
          style={{
            height: '360px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            gap: '10px',
            backgroundColor: '#f4f5f7',
          }}
        >
          {loading && (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner size="sm" />
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div
              className="d-flex justify-content-center align-items-center h-100 text-muted"
              style={{ fontSize: '0.875rem' }}
            >
              No messages yet. Start the conversation!
            </div>
          )}
          {!loading &&
            comments.map((c: any) => {
              const isOwn = c.author?.id === myId || !c.author;
              const authorName = c.author ? `${c.author.firstName} ${c.author.lastName}` : 'Unknown';
              const role = c.author?.group || null;
              return (
                <div
                  key={c.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isOwn ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: isOwn ? 'row-reverse' : 'row',
                      gap: '4px',
                      marginBottom: '3px',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ fontSize: '0.72rem', color: '#32325d' }}>{authorName}</strong>
                    {role && (
                      <span
                        style={{
                          fontSize: '0.65rem',
                          backgroundColor: '#e8eaf6',
                          color: '#3949ab',
                          borderRadius: '4px',
                          padding: '1px 6px',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {role}
                      </span>
                    )}
                    <span style={{ fontSize: '0.65rem', color: '#adb5bd' }}>{formatChatDate(c.createdAt)}</span>
                  </div>
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '8px 12px',
                      borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      backgroundColor: isOwn ? '#5e72e4' : '#ffffff',
                      color: isOwn ? '#ffffff' : '#32325d',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                      fontSize: '0.875rem',
                      wordBreak: 'break-word',
                    }}
                  >
                    {c.text}
                  </div>
                </div>
              );
            })}
          <div ref={bottomRef} />
        </div>
      </ModalBody>
      <ModalFooter style={{ padding: '8px 16px', gap: '8px' }}>
        <Input
          type="text"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          style={{ flex: 1 }}
        />
        <Button color="primary" onClick={handleSend} disabled={sending || !text.trim()} style={{ minWidth: '70px' }}>
          {sending ? <Spinner size="sm" /> : 'Send'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApprovalChatModal;
